// Google document URL detection utilities

/**
 * Regex pattern to detect Google Sheets URLs
 * Matches: docs.google.com/spreadsheets/d/{docId}
 */
const SHEETS_URL_REGEX = /docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/;

/**
 * Regex pattern to detect Google Docs URLs
 * Matches: docs.google.com/document/d/{docId}
 */
const DOCS_URL_REGEX = /docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)/;

/**
 * Checks if a URL is a Google Sheets URL
 *
 * Supported patterns:
 * - https://docs.google.com/spreadsheets/d/DOC_ID/edit
 * - https://docs.google.com/spreadsheets/d/DOC_ID/edit?usp=sharing
 * - https://docs.google.com/spreadsheets/d/DOC_ID/edit#gid=0
 * - https://docs.google.com/spreadsheets/d/DOC_ID
 * - https://docs.google.com/spreadsheets/d/DOC_ID/view
 *
 * @param url - The URL to check
 * @returns true if the URL is a Google Sheets URL
 */
export function isGoogleSheetsUrl(url: string): boolean {
  if (!url) return false;
  return SHEETS_URL_REGEX.test(url);
}

/**
 * Checks if a URL is a Google Docs URL
 *
 * Supported patterns:
 * - https://docs.google.com/document/d/DOC_ID/edit
 * - https://docs.google.com/document/d/DOC_ID/edit?usp=sharing
 * - https://docs.google.com/document/d/DOC_ID
 *
 * @param url - The URL to check
 * @returns true if the URL is a Google Docs URL
 */
export function isGoogleDocsUrl(url: string): boolean {
  if (!url) return false;
  return DOCS_URL_REGEX.test(url);
}

/**
 * Extracts the document ID from a Google Sheets or Docs URL
 *
 * @param url - The Google document URL
 * @returns The document ID, or null if not a valid Google document URL
 */
export function extractGoogleDocId(url: string): string | null {
  if (!url) return null;

  // Try Sheets first
  const sheetsMatch = url.match(SHEETS_URL_REGEX);
  if (sheetsMatch?.[1]) {
    return sheetsMatch[1];
  }

  // Try Docs
  const docsMatch = url.match(DOCS_URL_REGEX);
  if (docsMatch?.[1]) {
    return docsMatch[1];
  }

  return null;
}

/**
 * Determines the type of Google document from a URL
 *
 * @param url - The URL to check
 * @returns 'sheets' | 'docs' | null
 */
export function getGoogleDocType(url: string): 'sheets' | 'docs' | null {
  if (!url) return null;

  if (isGoogleSheetsUrl(url)) {
    return 'sheets';
  }

  if (isGoogleDocsUrl(url)) {
    return 'docs';
  }

  return null;
}

/**
 * Checks if a URL is any type of Google document (Sheets or Docs)
 *
 * @param url - The URL to check
 * @returns true if the URL is a Google Sheets or Docs URL
 */
export function isGoogleDocUrl(url: string): boolean {
  return isGoogleSheetsUrl(url) || isGoogleDocsUrl(url);
}
